import React, { useState } from 'react'
import { NavLink, useMatch } from 'react-router-dom'
import { HiDocumentSearch, HiCalculator, HiTemplate } from 'react-icons/hi'
import { FaBriefcase } from 'react-icons/fa'
import { IconType } from 'react-icons'
import { useTheme } from 'styled-components'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useAuthStore, useNavStore } from '../../state/store'
import OutsideAlerter from '../../hooks/ClickOutside'
import { useClientConfig } from '../../hooks/useClientConfig'
import { useBrokerActions } from '../../hooks/useBrokerActions'
import {
    StyledNavContainer,
    StyledNavTab,
    StyledNavIcon,
    StyledNavLabel,
    StyledBrokerContainer,
    StyledBrokerDropDown,
    StyledBrokerMenuItem,
    StyledBrokerMenuItemLink,
    StyledBrokerDivider,
} from './styles'

const ScreenerIcon = ({ color }: { color?: string }) => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M15.066 4.5885C16 5.86957 16.5023 7.41458 16.5 9C16.5 13.1422 13.1422 16.5 9 16.5C4.85775 16.5 1.5 13.1422 1.5 9C1.5 4.85775 4.85775 1.5 9 1.5V8.25M9 8.25C8.80109 8.25 8.61032 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9C8.25 9.19891 8.32902 9.38968 8.46967 9.53033C8.61032 9.67098 8.80109 9.75 9 9.75C9.19891 9.75 9.38968 9.67098 9.53033 9.53033C9.67098 9.38968 9.75 9.19891 9.75 9C9.75 8.80109 9.67098 8.61032 9.53033 8.46967C9.38968 8.32902 9.19891 8.25 9 8.25ZM9 4.875C8.07336 4.875 7.17371 5.187 6.44604 5.76072C5.71837 6.33444 5.20506 7.13646 4.98885 8.03753C4.77264 8.93859 4.86612 9.88621 5.25422 10.7277C5.64232 11.5691 6.30244 12.2554 7.12818 12.6759C7.95393 13.0963 8.8972 13.2266 9.80598 13.0455C10.7148 12.8644 11.5361 12.3827 12.1377 11.6778C12.7392 10.973 13.0859 10.0861 13.1219 9.16018C13.1579 8.23424 12.881 7.32315 12.336 6.57375"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="square"
        />
    </svg>
)

type NavItemProps = {
    to: string
    Icon: IconType
    name: string
    isHovered: boolean
    isAnyHovered: boolean
    onMouseEnter: () => void
    onMouseLeave: () => void
}

const NavItem = ({
    to,
    Icon,
    name,
    isHovered,
    isAnyHovered,
    onMouseEnter,
    onMouseLeave,
}: NavItemProps) => {
    const theme = useTheme()
    const isActive = !!useMatch(to)
    const showPill = isHovered || (isActive && !isAnyHovered)
    const showLabel = showPill
    const iconColor =
        isActive || isHovered
            ? theme.colours.font.green
            : theme.colours.font.primary
    return (
        <StyledNavTab
            as={NavLink}
            to={to}
            $showPill={showPill}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <StyledNavIcon>
                <Icon color={iconColor} />
            </StyledNavIcon>
            <StyledNavLabel $show={showLabel}>{name}</StyledNavLabel>
        </StyledNavTab>
    )
}

type AskAiNavItemProps = {
    isHovered: boolean
    isAnyHovered: boolean
    onMouseEnter: () => void
    onMouseLeave: () => void
}

const AskAiNavItem = ({
    isHovered,
    isAnyHovered,
    onMouseEnter,
    onMouseLeave,
}: AskAiNavItemProps) => {
    const theme = useTheme()
    const isActive = !!useMatch('/feature/chat')
    const showPill = isHovered || (isActive && !isAnyHovered)
    const showLabel = showPill
    const iconColor =
        isActive || isHovered
            ? theme.colours.font.green
            : theme.colours.font.primary
    return (
        <StyledNavTab
            as={NavLink}
            to="/feature/chat"
            $showPill={showPill}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <StyledNavIcon>
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M15.7509 4.38027L13.6209 2.25027C13.0359 1.66527 12.0834 1.66527 11.4984 2.25027L9.36844 4.38027C8.78344 4.96527 8.78344 5.91777 9.36844 6.50277L12.0234 9.15777C12.1734 9.30777 12.3609 9.37527 12.5559 9.37527C12.7509 9.37527 12.9384 9.30027 13.0884 9.15777L15.7434 6.50277C16.3284 5.91777 16.3284 4.96527 15.7434 4.38027H15.7509Z"
                        fill={iconColor}
                    />
                    <path
                        d="M6.75 3.75H2.25C1.83579 3.75 1.5 4.08579 1.5 4.5V9C1.5 9.41421 1.83579 9.75 2.25 9.75H6.75C7.16421 9.75 7.5 9.41421 7.5 9V4.5C7.5 4.08579 7.16421 3.75 6.75 3.75Z"
                        fill={iconColor}
                    />
                    <path
                        d="M13.5 10.5H9C8.58579 10.5 8.25 10.8358 8.25 11.25V15.75C8.25 16.1642 8.58579 16.5 9 16.5H13.5C13.9142 16.5 14.25 16.1642 14.25 15.75V11.25C14.25 10.8358 13.9142 10.5 13.5 10.5Z"
                        fill={iconColor}
                    />
                    <path
                        d="M6.75 10.5H2.25C1.83579 10.5 1.5 10.8358 1.5 11.25V15.75C1.5 16.1642 1.83579 16.5 2.25 16.5H6.75C7.16421 16.5 7.5 16.1642 7.5 15.75V11.25C7.5 10.8358 7.16421 10.5 6.75 10.5Z"
                        fill={iconColor}
                    />
                </svg>
            </StyledNavIcon>
            <StyledNavLabel $show={showLabel}>Builder</StyledNavLabel>
        </StyledNavTab>
    )
}

export const BrokerageNavItem = () => {
    const [isOpen, setIsOpen] = useState(false)
    const theme = useTheme()
    const { refreshSnapTrade, refreshSnapTradeTransactions } =
        useBrokerActions()

    return (
        <StyledBrokerContainer>
            <OutsideAlerter onpress={() => setIsOpen(false)}>
                <div
                    style={{ display: 'flex', alignItems: 'center' }}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <StyledNavIcon>
                        <FaBriefcase color={theme.colours.font.primary} />
                    </StyledNavIcon>
                </div>
                {isOpen && (
                    <StyledBrokerDropDown>
                        <StyledBrokerMenuItem>
                            <StyledBrokerMenuItemLink
                                href="https://wisesheets.io/connect-brokerage/manage"
                                target="_blank"
                            >
                                Manage Broker Connections
                            </StyledBrokerMenuItemLink>
                        </StyledBrokerMenuItem>
                        <StyledBrokerDivider />
                        <StyledBrokerMenuItem
                            onClick={() => {
                                setIsOpen(false)
                                refreshSnapTrade()
                            }}
                        >
                            Refresh Holdings
                        </StyledBrokerMenuItem>
                        <StyledBrokerDivider />
                        <StyledBrokerMenuItem
                            onClick={() => {
                                setIsOpen(false)
                                refreshSnapTradeTransactions()
                            }}
                        >
                            Refresh Transactions
                        </StyledBrokerMenuItem>
                    </StyledBrokerDropDown>
                )}
            </OutsideAlerter>
        </StyledBrokerContainer>
    )
}

const Nav = React.memo(() => {
    const { isWisesheets } = useClientConfig()
    const { isCollapsed } = useNavStore((state) => state)
    const aiChatEnabled = useFeatureIsOn('ai-chat')
    const { currentUser } = useAuthStore((state) => state)
    const showAiChat = aiChatEnabled && isWisesheets && currentUser.isSubscribed
    const [hoveredTab, setHoveredTab] = useState<string | null>(null)

    if (isCollapsed) return null

    const isAnyHovered = hoveredTab !== null

    return (
        <StyledNavContainer>
            {showAiChat ? (
                <AskAiNavItem
                    isHovered={hoveredTab === 'chat'}
                    isAnyHovered={isAnyHovered}
                    onMouseEnter={() => setHoveredTab('chat')}
                    onMouseLeave={() => setHoveredTab(null)}
                />
            ) : null}
            <NavItem
                to="/feature/statement-dump"
                Icon={HiDocumentSearch}
                name="Financials"
                isHovered={hoveredTab === 'statement-dump'}
                isAnyHovered={isAnyHovered}
                onMouseEnter={() => setHoveredTab('statement-dump')}
                onMouseLeave={() => setHoveredTab(null)}
            />
            <NavItem
                to="/feature/function-builder"
                Icon={HiCalculator}
                name="Functions"
                isHovered={hoveredTab === 'function-builder'}
                isAnyHovered={isAnyHovered}
                onMouseEnter={() => setHoveredTab('function-builder')}
                onMouseLeave={() => setHoveredTab(null)}
            />
            <NavItem
                to="/feature/screener"
                Icon={ScreenerIcon}
                name="Screener"
                isHovered={hoveredTab === 'screener'}
                isAnyHovered={isAnyHovered}
                onMouseEnter={() => setHoveredTab('screener')}
                onMouseLeave={() => setHoveredTab(null)}
            />
            {isWisesheets ? (
                <NavItem
                    to="/feature/templates"
                    Icon={HiTemplate}
                    name="Templates"
                    isHovered={hoveredTab === 'templates'}
                    isAnyHovered={isAnyHovered}
                    onMouseEnter={() => setHoveredTab('templates')}
                    onMouseLeave={() => setHoveredTab(null)}
                />
            ) : null}
        </StyledNavContainer>
    )
})

Nav.displayName = 'Nav'

export default Nav
